import type {
  MaintenanceCostSplit,
  MaintenanceOwnerShare,
  MaintenanceRecord,
} from '../../types';
import {
  COST_SPLIT_OPTIONS,
  costSplitRows,
  costSplitTitle,
  formatRub,
} from '../../utils/maintenanceCost';
import { parseManualShareInput } from '../../utils/expenseSplit';
import ManualShareEditor, {
  parseCostAmount,
} from '../ManualShareEditor/ManualShareEditor';
import './MaintenanceCostFields.css';

type Props = {
  cost: string;
  costSplit: MaintenanceCostSplit;
  manualShares: Record<number, string>;
  owners: MaintenanceOwnerShare[];
  record?: MaintenanceRecord | null;
  radioName?: string;
  onCostChange: (value: string) => void;
  onCostSplitChange: (value: MaintenanceCostSplit) => void;
  onManualSharesChange: (value: Record<number, string>) => void;
};

const previewFor = (
  record: MaintenanceRecord | null | undefined,
  owners: MaintenanceOwnerShare[],
  costSplit: MaintenanceCostSplit,
  manualShares: Record<number, string>
): MaintenanceRecord => {
  const allocations = owners
    .filter(
      (owner) => parseManualShareInput(manualShares[owner.user_id] || '') > 0
    )
    .map((owner) => ({
      user_id: owner.user_id,
      share: parseManualShareInput(manualShares[owner.user_id] || ''),
    }));
  const base: MaintenanceRecord = record || {
    id: 0,
    schedule_id: 0,
    mechanic: '',
    date: '',
    hobbs: 0,
    owners,
    cost: null,
    cost_split: costSplit,
    pilot_data: { total_hours: 0, pilots: [] },
  };
  return {
    ...base,
    owners,
    cost_split: costSplit,
    pilot_data: {
      ...base.pilot_data,
      split: costSplit,
      allocations: costSplit === 'manual' ? allocations : base.pilot_data?.allocations,
    },
  };
};

const MaintenanceCostFields = ({
  cost,
  costSplit,
  manualShares,
  owners,
  record,
  radioName = 'to-cost-split',
  onCostChange,
  onCostSplitChange,
  onManualSharesChange,
}: Props) => {
  const preview = previewFor(record, owners, costSplit, manualShares);
  const rows = costSplitRows(preview, cost, costSplit);
  const amount = parseCostAmount(cost);

  return (
    <>
      <label>Стоимость, ₽</label>
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder="необязательно"
        value={cost}
        onChange={(e) => onCostChange(e.target.value)}
      />
      <label>Распределение:</label>
      <fieldset className="cost-split">
        {COST_SPLIT_OPTIONS.map((option) => (
          <label key={option.value}>
            <input
              type="radio"
              name={radioName}
              checked={costSplit === option.value}
              onChange={() => onCostSplitChange(option.value)}
            />
            {option.label}
          </label>
        ))}
      </fieldset>
      {costSplit === 'manual' ? (
        <>
          <p className="cost-empty">
            Отметьте участников. Можно ввести доли или суммы в рублях.
          </p>
          <ManualShareEditor
            owners={owners}
            amount={amount}
            manualShares={manualShares}
            onManualSharesChange={onManualSharesChange}
            noOwnersHint="Нет долей владения на дату ТО"
          />
        </>
      ) : null}

      {rows.length > 0 ? (
        <>
          <label>{costSplitTitle(costSplit)}</label>
          <ul className="cost-preview">
            {rows.map((row) => (
              <li key={row.key}>
                {row.name}: {row.detail}
                {row.cost != null ? ` — ${formatRub(row.cost)}` : ''}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="cost-empty">
          {costSplit === 'manual' || costSplit === 'shares'
            ? 'Нет долей владения на дату ТО'
            : 'Распределение по налёту появится после сохранения'}
        </p>
      )}
    </>
  );
};

export default MaintenanceCostFields;
